import React, { useState, useEffect, useCallback, useRef } from 'react';
import FilterTemplate from '../components/filtertemplate/FilterTemplate';
import { CalendarIcon, ClockIcon, UserIcon, SwapIcon, CloseIcon } from '../components/shared/Icons';
import { fetchReplacements, handleTimeSubmit, downloadReplacement } from '../utils/api';
import CustomSelect from '../components/shared/CustomSelect';
import HelpButton from '../components/shared/HelpButton';
import { useUser } from '../utils/UserContext';
import { cssUrl } from '../utils/cssVersion';
import { rolesAccess } from '../data/map';
import FilterChips from '../components/shared/FilterChips';
import { CHIP_GROUP_COLORS } from '../components/shared/chipColors';

const filterReplacementStyles = cssUrl('filterTemplate.css');



const DAY_NAMES = ['Mon', 'Tues', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'];
const JS_DAY_TO_INDEX = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5, 0: 6 };

// Raw row schema in /api/replacement/existing (matches NewReplacement.calculateUsedHoursPerDate):
// [id, start, end, site, hrs, rStatus, rByStatus, replaced, replacer, mon..sun(9..15), owner]
const RAW_DAY_COL_OFFSET = 9;
const RAW_OWNER_COL = 16;
const RAW_SITE_COL = 3;
const RAW_HOURS_COL = 4;

// Shift data row schema: [..., 1=owner, 2=site, 3=shiftHours, 4..10=mon..sun, 11=daystatus]
const SHIFT_DAY_DATA_OFFSET = 4;

// ─────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────
const displayDate = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};

const parseLocalDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
};

const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// True if dateStr (YYYY-MM-DD) is in a calendar month before today's month.
// Mirrors NewReplacement.isPriorMonth so behaviour stays consistent.
const isPriorMonth = (dateStr) => {
  if (!dateStr) return false;
  const [y, m] = dateStr.split('-').map(Number);
  const now = new Date();
  return y < now.getFullYear() || (y === now.getFullYear() && m - 1 < now.getMonth());
};

// Find the raw replacement row by id. The card item is display-shaped; the raw
// row has the per-day flags and site we need for cross-record capacity checks.
const findRawReplacement = (recordId, existingReplacements) => {
  if (recordId == null) return null;
  return existingReplacements.find(r => String(r[0]) === String(recordId)) || null;
};

// Find this owner's shift layout for the given site, searching both TFN and ABN.
// Owner could be either status — we don't need to know in advance.
const findShiftLayout = (ownerName, site, tfnShiftData, abnShiftData) => {
  const search = (data, status) => {
    for (const row of data) {
      if (row[1] === ownerName && row[2] === site) {
        const hours = [];
        for (let i = 0; i < 7; i++) {
          hours.push(Number(row[SHIFT_DAY_DATA_OFFSET + i]) || 0);
        }
        return { hours, shiftHours: Number(row[3]) || 0, ownerStatus: status };
      }
    }
    return null;
  };
  return search(tfnShiftData, 'TFN') || search(abnShiftData, 'ABN');
};

// Per-date hours used by OTHER replacements (excludes the record being edited).
const calculateUsedHoursExcluding = (startStr, endStr, site, ownerName, existingReplacements, excludeId) => {
  const perDate = {};
  if (!startStr || !existingReplacements.length) return perDate;

  const start = parseLocalDate(startStr);
  const end = parseLocalDate(endStr || startStr);

  const cursor = new Date(start);
  while (cursor <= end) {
    const dayIndex = JS_DAY_TO_INDEX[cursor.getDay()];
    const dayCol = RAW_DAY_COL_OFFSET + dayIndex;
    const dateStr = formatLocalDate(cursor);

    for (const rep of existingReplacements) {
      if (String(rep[0]) === String(excludeId)) continue;
      if (
        dateStr >= rep[1] &&
        dateStr <= rep[2] &&
        site === rep[RAW_SITE_COL] &&
        ownerName === rep[RAW_OWNER_COL] &&
        Number(rep[dayCol]) === 1
      ) {
        perDate[dateStr] = (perDate[dateStr] || 0) + (Number(rep[RAW_HOURS_COL]) || 0);
      }
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return perDate;
};

// Parse the card's day string ("Mon,Wed,Fri,") into a 7-element boolean array.
// Used as a fallback when the raw existing row isn't yet loaded.
const parseDaysString = (daysStr) => {
  const flags = new Array(7).fill(false);
  if (!daysStr) return flags;
  DAY_NAMES.forEach((name, i) => {
    if (daysStr.includes(name)) flags[i] = true;
  });
  return flags;
};

// ─────────────────────────────────────────────
//  REPLACEMENT CARD
// ─────────────────────────────────────────────
const ReplacementCard = ({ item, onClick, canEdit }) => (
  <div
    className="fr-card"
    onClick={canEdit ? () => onClick(item) : undefined}
    style={!canEdit ? { cursor: 'default' } : undefined}
  >
    <div className="fr-card-header">
      <div className="fr-card-dates">
        <CalendarIcon />
        <span className="fr-card-date-start">{displayDate(item[1])}</span>
        <span className="fr-card-date-sep">→</span>
        <span className="fr-card-date-end">{displayDate(item[2])}</span>
      </div>
      <div className="fr-card-status-badge" data-status={item[6]?.toLowerCase()}>
        {item[6]}
      </div>
    </div>

    <div className="fr-card-body">
      <div className="fr-card-row">
        <UserIcon />
        <span className="fr-card-label">Owner:</span>
        <span className="fr-card-value">{item[5]}</span>
        <span className="fr-card-status-tag" data-status={item[6]?.toLowerCase()}>
          {item[6]}
        </span>
      </div>

      <div className="fr-card-row">
        <SwapIcon />
        <span className="fr-card-label">Replaced:</span>
        <span className="fr-card-value">{item[7]}</span>
        <span className="fr-card-status-tag" data-status={item[8]?.toLowerCase()}>
          {item[8]}
        </span>
      </div>

      <div className="fr-card-row">
        <UserIcon />
        <span className="fr-card-label">Replaced by:</span>
        <span className="fr-card-value">{item[9]}</span>
        <span className="fr-card-status-tag" data-status={item[10]?.toLowerCase()}>
          {item[10]}
        </span>
      </div>

      {item[12] && (
        <div className="fr-card-row">
          <span className="fr-card-label">Site:</span>
          <span className="fr-card-value">{item[12]}</span>
        </div>
      )}
    </div>

    <div className="fr-card-footer">
      <div className="fr-card-meta">
        <span className="fr-card-meta-item">
          <CalendarIcon />
          Weekdays: {item[3]} &nbsp; Weekend: {item[4]}
        </span>
      </div>
      <div className="fr-card-meta">
        <span className="fr-card-meta-item">Days: {item[13]}</span>
      </div>
      <div className="fr-card-hours">
        <ClockIcon />
        <span>{item[11]} hrs/shift</span>
      </div>
    </div>

    {canEdit && <div className="fr-card-tap-hint">Tap to update time</div>}
  </div>
);

// ─────────────────────────────────────────────
//  UPDATE TIME MODAL
//  - Validates against shift cap + other replacements' usage
//  - Read-only when operations user views a prior-month record
// ─────────────────────────────────────────────
const UpdateTimeModal = ({
  item,
  onClose,
  onSubmit,
  isSubmitting,
  onToast,
  cleaners,
  isAdmin,
  isOperations,
  tfnShiftData,
  abnShiftData,
  existingReplacements,
}) => {
  const [timeValue, setTimeValue] = useState(item?.[11]?.toString() || '');
  const [selectedCleaner, setSelectedCleaner] = useState(item?.[9]?.toString() || '');

  // ── Role gate: prior-month is read-only for operations (not admin) ──
  // Gated on record's start date — stays correct regardless of month filter.
  const isReadOnly = !isAdmin && isOperations && isPriorMonth(item?.[1]);

  // ── Pull validation context from the card (site/owner) + raw row (day flags) ──
  const recordId = item?.[0];
  const ownerName = item?.[5] || '';
  const site = item?.[12] || '';
  const startDate = item?.[1] || '';
  const endDate = item?.[2] || '';

  // Day flags: prefer raw row when loaded, fall back to parsing card's days string
  const rawRecord = findRawReplacement(recordId, existingReplacements);
  const dayFlags = rawRecord
    ? Array.from({ length: 7 }, (_, i) => Number(rawRecord[RAW_DAY_COL_OFFSET + i]) === 1)
    : parseDaysString(item?.[13]);

  // Owner's shift layout for this site (hours cap per weekday)
  const shiftLayout = findShiftLayout(ownerName, site, tfnShiftData, abnShiftData);

  // Hours allocated to this owner+site by OTHER replacements
  const usedHoursPerDate = calculateUsedHoursExcluding(
    startDate, endDate, site, ownerName, existingReplacements, recordId
  );

  const hasValidationContext = !!shiftLayout && !!site;

  // ── Tightest cap across every covered date ──
  const computeMaxHours = () => {
    if (!shiftLayout || !startDate) return Infinity;
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate || startDate);
    const cursor = new Date(start);
    let minAvailable = Infinity;
    while (cursor <= end) {
      const di = JS_DAY_TO_INDEX[cursor.getDay()];
      if (dayFlags[di] && shiftLayout.hours[di] > 0) {
        const dateStr = formatLocalDate(cursor);
        const used = usedHoursPerDate[dateStr] || 0;
        const available = shiftLayout.hours[di] - used;
        if (available < minAvailable) minAvailable = available;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return minAvailable === Infinity ? Infinity : Math.max(0, minAvailable);
  };

  const maxHours = computeMaxHours();

  // ── Per-date check identical to NewReplacement.handleHoursChange ──
  const handleTimeChange = (e) => {
    if (isReadOnly) return;
    const val = e.target.value.replace(/[^0-9.]/g, '');
    const num = parseFloat(val);

    if (val && !isNaN(num) && num < 0) {
      onToast?.({ type: 'error', message: 'Hours must be zero or greater' });
      return;
    }

    if (hasValidationContext && val && !isNaN(num) && num > 0) {
      const start = parseLocalDate(startDate);
      const end = parseLocalDate(endDate || startDate);
      const cursor = new Date(start);

      while (cursor <= end) {
        const di = JS_DAY_TO_INDEX[cursor.getDay()];
        if (dayFlags[di] && shiftLayout.hours[di] > 0) {
          const dateStr = formatLocalDate(cursor);
          const shiftHoursForDay = shiftLayout.hours[di];
          const alreadyUsed = usedHoursPerDate[dateStr] || 0;
          const totalForDay = num + alreadyUsed;
          if (totalForDay > shiftHoursForDay) {
            const remaining = Math.max(0, shiftHoursForDay - alreadyUsed);
            onToast?.({
              type: 'error',
              message: remaining <= 0
                ? `${dateStr} (${DAY_NAMES[di]}) is fully allocated — no hours available`
                : `${dateStr} (${DAY_NAMES[di]}) only has ${remaining.toFixed(1)} hours available`,
            });
            return;
          }
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    setTimeValue(val);
  };

  // ── Submit guard ──
  const isValid = () => {
    if (isReadOnly) return false;
    const num = parseFloat(timeValue);
    if (isNaN(num) || num < 0) return false;
    if (!hasValidationContext) return false; // require validation context before allowing submit
    if (num === 0) return true;              // 0 = un-allocate, always allowed
    return num <= maxHours;
  };


const toNumberString = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const cleaned = String(val).replace(/[$,\s]/g, '');
    if (cleaned === '' || cleaned === '-') return '';
    const num = parseFloat(cleaned);
    return isNaN(num) ? '' : String(num);
  };
  
  const handleSubmit = () => {
    if (!isValid()) {
      onToast?.({
        type: 'error',
        message: hasValidationContext
          ? `Hours exceed available capacity. Max allowed: ${isFinite(maxHours) ? maxHours.toFixed(1) : 'n/a'}`
          : 'Shift data not loaded yet — please wait a moment and try again',
      });
      return;
    }
    onSubmit({
      recordId,
      cleanerReplacing: selectedCleaner,
      timeReplacing: toNumberString(timeValue) || "0",
    });
  };

  return (
    <div className="fr-modal-overlay" onClick={onClose}>
      <div className="fr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fr-modal-handle" />
        <div className="fr-modal-header">
          <h3 className="fr-modal-title">
            {isReadOnly ? 'View Replacement (read-only)' : 'Update Replacement Time'}
          </h3>
          <button className="fr-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="fr-modal-body">
          {isReadOnly && (
            <div
              className="ob-info-banner ob-info-banner-warning"
              style={{ marginBottom: 12, fontSize: '0.8rem' }}
            >
              Prior-month records are read-only for Operations users. Contact an Admin if a change is needed.
            </div>
          )}

          <div className="fr-modal-field">
            <label className="fr-modal-label">Owner Shift</label>
            <div className="fr-modal-value">{item[5]}</div>
          </div>

          {site && (
            <div className="fr-modal-field">
              <label className="fr-modal-label">Site</label>
              <div className="fr-modal-value">{site}</div>
            </div>
          )}

          <CustomSelect
            label="Replaced by"
            value={selectedCleaner}
            onChange={isReadOnly ? () => {} : setSelectedCleaner}
            options={cleaners}
            disabled={isReadOnly}
          />

          <div className="fr-modal-field">
            <label className="fr-modal-label">
              Time Replacing
              {hasValidationContext && isFinite(maxHours) && !isReadOnly && (
                <span style={{ marginLeft: 8, fontSize: '0.75rem', opacity: 0.7 }}>
                  (max {maxHours.toFixed(1)} hrs/day)
                </span>
              )}
            </label>
            <input
              type="number"
              step="0.25"
              min="0"
              max={isFinite(maxHours) ? maxHours : undefined}
              className="fr-modal-input"
              value={timeValue}
              onChange={handleTimeChange}
              placeholder="Enter hours"
              disabled={isReadOnly}
              readOnly={isReadOnly}
            />
            {!isReadOnly && hasValidationContext && Object.keys(usedHoursPerDate).length > 0 && (
              <div
                className="ob-info-banner ob-info-banner-warning"
                style={{ marginTop: 8, fontSize: '0.75rem' }}
              >
                Other replacements already use hours on:&nbsp;
                {Object.entries(usedHoursPerDate)
                  .map(([d, h]) => `${d} (${h.toFixed(1)}h)`)
                  .join(', ')}
              </div>
            )}
            {!isReadOnly && !hasValidationContext && (
              <div style={{ marginTop: 8, fontSize: '0.75rem', opacity: 0.6 }}>
                Loading shift data — submit will enable once validation is ready.
              </div>
            )}
          </div>
        </div>

        <div className="fr-modal-actions">
          <button className="fr-modal-btn fr-btn-cancel" onClick={onClose}>
            Close
          </button>
          {!isReadOnly && (
            <button
              className="fr-modal-btn fr-btn-submit"
              onClick={handleSubmit}
              disabled={isSubmitting || !isValid()}
            >
              {isSubmitting ? 'Processing...' : 'Process Time'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
//  CHIP CONFIG
// ─────────────────────────────────────────────
const filterChipGroups = [
  { color: CHIP_GROUP_COLORS.cyan, values: ['ABN'] },
  { color: CHIP_GROUP_COLORS.violet, values: ['TFN'] },
];

// ─────────────────────────────────────────────
//  MAIN
//  - Fetch tfn-shifts, abn-shifts, existing once at mount (for editors only).
//  - Refetch existing after each successful submit.
//  - Only admin/operations can tap cards.
// ─────────────────────────────────────────────
const FilterReplacement = ({ showHeader = false }) => {
  const { user } = useUser();
  const isAdmin = rolesAccess.admin?.includes(user?.userJob || '') || false;
  const isOperations = rolesAccess.supervisor?.includes(user?.userJob || '') || false;
  const isManagement = rolesAccess.management.includes(user?.userJob || '');
  const canEdit = isAdmin || isOperations || isManagement;

  const [tfnShiftData, setTfnShiftData] = useState([]);
  const [abnShiftData, setAbnShiftData] = useState([]);
  const [existingReplacements, setExistingReplacements] = useState([]);
  const hasFetchedRef = useRef(false);

  // Only fetch validation data for users who can edit — viewers never open the modal.
  useEffect(() => {
    if (!canEdit || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const loadValidationData = async () => {
      try {
        const [tfnRes, abnRes, existingRes] = await Promise.all([
          fetch('/api/replacement/tfn-shifts'),
          fetch('/api/replacement/abn-shifts'),
          fetch('/api/replacement/existing'),
        ]);
        const [tfnData, abnData, existingData] = await Promise.all([
          tfnRes.json(),
          abnRes.json(),
          existingRes.json(),
        ]);
        setTfnShiftData(tfnData.all || []);
        setAbnShiftData(abnData.all || []);
        setExistingReplacements(existingData.all || []);
      } catch (err) {
        console.error('Failed to load replacement validation data:', err);
      }
    };
    loadValidationData();
  }, [canEdit]);

  const refetchExisting = useCallback(async () => {
    try {
      const res = await fetch('/api/replacement/existing');
      const data = await res.json();
      setExistingReplacements(data.all || []);
    } catch (err) {
      console.error('Failed to refresh existing replacements:', err);
    }
  }, []);

  const submitWithRefresh = useCallback(async (payload) => {
    const result = await handleTimeSubmit(payload);
    if (result?.status === 'success' || result?.status === 'partial') {
      refetchExisting();
    }
    return result;
  }, [refetchExisting]);

  return (
    <FilterTemplate
      title="Replacements"
      helpButton={
        <HelpButton
          helpData={[
            { chip: 'replacement_filter', urlFilter: '/static/pdffilters/ReplacementFilterGuide.pdf' },
          ]}
        />
      }
      subtitle="Replacement Leave"
      searchPlaceholder="Search by name, site, or days..."
      stylesheetHref={filterReplacementStyles}
      fetchData={fetchReplacements}
      dataKey="replacements"
      extraDataKeys={['cleaners']}
      // Search fields: 12 (site), 13 (days string). Was [1, 5, 6, 7, 9, 12] with 12=days.
      searchFields={[1, 5, 6, 7, 9, 12, 13]}
      onSubmit={submitWithRefresh}
      loadingMessage="Loading replacements..."
      showNavigation={true}
      chipLogic="and"
      onDownload={(isAdmin || isManagement) && downloadReplacement}
      showHeader={showHeader}
      renderChips={(chipQuery, setChipQuery, chipsOpen, setChipsOpen) => (
        <FilterChips
          chipGroups={filterChipGroups}
          chipQuery={chipQuery}
          setChipQuery={setChipQuery}
          chipsOpen={chipsOpen}
          setChipsOpen={setChipsOpen}
        />
      )}
      renderCard={(item, onClick) => (
        <ReplacementCard item={item} onClick={onClick} canEdit={canEdit} />
      )}
      renderModal={(item, onClose, onSubmit, isSubmitting, setToast, extraData) =>
        canEdit ? (
          <UpdateTimeModal
            item={item}
            onClose={onClose}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            onToast={setToast}
            cleaners={extraData.cleaners || []}
            isAdmin={isAdmin}
            isOperations={isOperations}
            tfnShiftData={tfnShiftData}
            abnShiftData={abnShiftData}
            existingReplacements={existingReplacements}
          />
        ) : null
      }
    />
  );
};

export default FilterReplacement;